import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Package, MessageSquare, CreditCard, Star, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

const ICON_MAP = {
  booking_new: { icon: Package, color: "bg-blue-50 text-blue-600" },
  booking_confirmed: { icon: Package, color: "bg-emerald-50 text-emerald-600" },
  booking_returned: { icon: Package, color: "bg-amber-50 text-amber-600" },
  booking_cancelled: { icon: Package, color: "bg-red-50 text-red-600" },
  message_new: { icon: MessageSquare, color: "bg-violet-50 text-violet-600" },
  payout_completed: { icon: CreditCard, color: "bg-emerald-50 text-emerald-600" },
  review_received: { icon: Star, color: "bg-orange-50 text-orange-600" },
  system: { icon: ShieldCheck, color: "bg-stone-100 text-stone-600" },
};

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/notifications", { params: { limit: 50 } })
      .then((res) => setNotifications(res.data.notifications || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const markAllRead = async () => {
    try {
      await api.put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { console.error(e); }
  };

  const handleClick = async (notif) => {
    if (!notif.read) {
      await api.put(`/notifications/${notif.id}/read`).catch(() => {});
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, read: true } : n));
    }
    if (notif.link) navigate(notif.link);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-stone-400 hover:text-stone-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-heading font-bold text-xl text-stone-900">Notifications</h1>
          {unreadCount > 0 && (
            <span className="bg-coral text-white text-xs font-bold px-2 py-0.5 rounded-full">{unreadCount}</span>
          )}
        </div>
        {unreadCount > 0 && (
          <Button variant="ghost" size="sm" className="text-xs gap-1 text-stone-500" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4" /> Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-stone-300 border-t-coral rounded-full animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16">
          <Bell className="w-12 h-12 mx-auto text-stone-200 mb-4" />
          <p className="text-stone-500">No notifications yet</p>
          <p className="text-stone-400 text-sm mt-1">You'll be notified when someone books your items, sends a message, or more.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden divide-y divide-stone-100">
          {notifications.map((notif) => {
            const { icon: Icon, color } = ICON_MAP[notif.type] || ICON_MAP.system;
            return (
              <button
                key={notif.id}
                onClick={() => handleClick(notif)}
                className={`w-full p-4 flex items-start gap-3 text-left hover:bg-stone-50 transition-colors ${!notif.read ? "bg-blue-50/30" : ""}`}
                data-testid={`notification-${notif.id}`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm ${!notif.read ? "font-semibold text-stone-900" : "font-medium text-stone-700"}`}>{notif.title}</p>
                    {!notif.read && <span className="w-2 h-2 rounded-full bg-coral shrink-0" />}
                  </div>
                  <p className="text-sm text-stone-500 mt-0.5 line-clamp-2">{notif.message}</p>
                  <p className="text-xs text-stone-400 mt-1">{timeAgo(notif.created_at)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
